import { Link, useLocation, useParams } from 'react-router'
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/20/solid'
import { useSelector } from 'react-redux'
import { pageTitle } from '../../features/PageTitleSlice.ts'
import { useGetClubDetailsQuery } from '../../features/SpeechAgendaApiSlice.ts'
import { useAppAuth } from '../../app/hooks/UseAppAuth.ts'
import { useMemo } from 'react'

type BreadcrumbTrailItem = {
  id: string
  label: string
  to?: string
  isCurrent?: boolean
  isLoading?: boolean
}

const BREADCRUMB_LIMIT = 4

function getSkeletonWidth(id: string) {
  switch (id) {
    case 'club':
      return 'w-32'
    case 'meeting':
      return 'w-36'
    default:
      return 'w-24'
  }
}

function SkeletonLoader({ id }: { id: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-3 rounded bg-gray-200 align-middle animate-pulse ${getSkeletonWidth(id)}`}
    />
  )
}

type BreadcrumbItemProps = {
  crumb: BreadcrumbTrailItem
  showChevron: boolean
}

function BreadcrumbItem({ crumb, showChevron }: BreadcrumbItemProps) {
  const isLink = Boolean(crumb.to) && !crumb.isCurrent && !crumb.isLoading

  return (
    <li key={crumb.id} className="flex items-center">
      {showChevron && (
        <ChevronRightIcon
          aria-hidden="true"
          className="h-4 w-4 flex-shrink-0 text-gray-400"
        />
      )}
      {isLink ? (
        <Link
          to={crumb.to!}
          className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">
          {crumb.label}
        </Link>
      ) : (
        <span
          className={`ml-4 text-sm font-medium ${
            crumb.isCurrent ? 'text-gray-900' : 'text-gray-500'
          }`}
          {...(crumb.isCurrent ? { 'aria-current': 'page' } : {})}>
          {crumb.isLoading ? <SkeletonLoader id={crumb.id} /> : crumb.label}
        </span>
      )}
    </li>
  )
}

export default function Breadcrumbs() {
  const location = useLocation()
  const rawPathname = location.pathname
  const normalizedPathname = useMemo(() => {
    if (rawPathname === '/') {
      return rawPathname
    }
    return rawPathname.replace(/\/+$/, '')
  }, [rawPathname])

  const { clubId, meetingTimestampUtc } = useParams()
  const { userLoaded } = useAppAuth()
  const title = useSelector(pageTitle)

  const isClubContext = normalizedPathname.startsWith('/club/')
  const shouldFetchClubDetails =
    isClubContext && Boolean(clubId) && Boolean(userLoaded)

  const {
    data: clubDetails,
    isFetching,
    isLoading,
  } = useGetClubDetailsQuery(clubId ?? '', {
    skip: !shouldFetchClubDetails,
  })

  const clubName = clubDetails?.clubName
  const clubBreadcrumbLoading =
    shouldFetchClubDetails && (isFetching || isLoading || !clubName)

  const breadcrumbTrail = useMemo(() => {
    const trail: BreadcrumbTrailItem[] = []

    if (normalizedPathname === '/clubs') {
      trail.push({
        id: 'clubs',
        label: title || 'Clubs',
        to: '/clubs',
        isCurrent: true,
        isLoading: !title,
      })
    } else if (isClubContext) {
      trail.push({
        id: 'clubs',
        label: 'Clubs',
        to: '/clubs',
      })

      const clubPath = clubId ? `/club/${clubId}` : undefined
      if (clubPath) {
        trail.push({
          id: 'club',
          label: clubName || 'Club',
          to: clubPath,
          isCurrent: normalizedPathname === clubPath,
          isLoading: clubBreadcrumbLoading,
        })

        if (normalizedPathname === `${clubPath}/admin`) {
          trail.push({
            id: 'admin',
            label: 'Admin',
            to: `${clubPath}/admin`,
            isCurrent: true,
          })
        } else if (
          meetingTimestampUtc &&
          normalizedPathname === `${clubPath}/meeting/${meetingTimestampUtc}`
        ) {
          trail.push({
            id: 'meeting',
            label: title || 'Meeting',
            to: `${clubPath}/meeting/${meetingTimestampUtc}`,
            isCurrent: true,
            isLoading: !title,
          })
        }
      }
    } else if (normalizedPathname === '/profile') {
      trail.push({
        id: 'profile',
        label: title || 'Profile',
        to: '/profile',
        isCurrent: true,
        isLoading: !title,
      })
    }

    return trail
  }, [
    normalizedPathname,
    clubId,
    meetingTimestampUtc,
    title,
    clubName,
    clubBreadcrumbLoading,
    isClubContext,
  ])

  // +1 for the Home link
  const hideHomeLink = breadcrumbTrail.length + 1 >= BREADCRUMB_LIMIT
  const visibleBreadcrumbs = hideHomeLink
    ? breadcrumbTrail.slice(-BREADCRUMB_LIMIT)
    : breadcrumbTrail

  const showChevronArrow = (index: number) => index !== 0 || !hideHomeLink

  return (
    <nav
      className="border-b border-t border-gray-200 bg-white"
      aria-label="Breadcrumb">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 custom-max-w-920px">
        <ol
          role="list"
          className={`flex items-center space-x-4 py-4 ${hideHomeLink ? '-ml-4' : ''}`}>
          {!hideHomeLink && (
            <li>
              <div className="flex items-center">
                <Link
                  to="/"
                  className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700">
                  <HomeIcon
                    aria-hidden="true"
                    className="h-4 w-4 flex-shrink-0 text-gray-400"
                  />
                  <span className="ml-2">Home</span>
                </Link>
              </div>
            </li>
          )}
          {visibleBreadcrumbs.map((crumb, index) => (
            <BreadcrumbItem
              key={crumb.id}
              crumb={crumb}
              showChevron={showChevronArrow(index)}
            />
          ))}
        </ol>
      </div>
    </nav>
  )
}
